import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ComponentFactoryResolver, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { debounce } from 'src/app/directives/debounce.decorator';
import { DynamicComponentDirective } from 'src/app/directives/dynamic-component.directive';
import { ChatService } from 'src/app/services/chat.service';
import { BaseWidgetComponent } from '../strategy-grid/base-widget/base-widget.component';
import { IdentityService } from 'src/app/services/identity.service';
import { MuxSessionService } from 'src/app/services/mux-session.service';
import { SettingsService } from 'src/app/services/settings.service';
import * as htmlToImage from 'html-to-image';

export const REACTION_EMOJIS = ['👍', '👎', '🎯', '🔥', '😕', '❤️', '🙏', '💯', '🤣', '🤔', '👀', '🚀'];

export const REACTION_TOOLTIPS: Record<string, string> = {
  '👍': 'Agree',
  '👎': 'Disagree',
  '🎯': 'Spot on',
  '🔥': 'Impressive',
  '😕': 'Confused',
  '❤️': 'Love',
  '🙏': 'Thanks',
  '💯': 'Perfect',
  '🤣': 'Hilarious',
  '🤔': 'Thinking',
  '👀': 'Looking into it',
  '🚀': 'Ship it',
};

@Component({
    selector: 'app-chat-screen',
    templateUrl: './chat-screen.component.html',
    styleUrls: ['./chat-screen.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ChatScreenComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('content', { static: true }) private _content: ElementRef<HTMLDivElement>;
  @ViewChildren(DynamicComponentDirective) dynamicHosts!: QueryList<DynamicComponentDirective>;

  // Lightbox state
  public lightboxOpen = false;
  public lightboxImageSrc = '';

  // Snapshot preview state
  public previewingMessage: ChatService.ChatMessage | null = null;
  public previewPosition = { x: 0, y: 0 };

  // Pinned message state — emits when pins change so parent can refresh pins pane
  @Output() pinsChanged = new EventEmitter<void>();

  // ResizeObserver for widget auto-scroll
  private widgetResizeObserver: ResizeObserver | null = null;
  private observedWidgets = new Set<Element>();

  // ResizeObserver for content height changes (scroll to bottom when content grows)
  private contentResizeObserver: ResizeObserver | null = null;
  private lastKnownScrollHeight = 0;

  // Track if we're currently handling a streaming message
  private isProcessingStream = false;

  // Readback mode: pause auto-scroll when user scrolls up to read earlier content
  private isInReadbackMode = false;
  // Threshold in pixels from bottom to consider "at bottom"
  private readonly SCROLL_THRESHOLD = 100;
  // Bound scroll handler for cleanup
  private boundScrollHandler: (() => void) | null = null;
  // Browser-tab background/foreground (flipping to another app or tab). Distinct
  // from the dockview panel hide handled by the MutationObserver below.
  private boundVisibilityHandler: (() => void) | null = null;

  // Scroll-to-bottom button state
  public showScrollToBottom = false;
  public newContentCount = 0;
  // Track content height when entering readback mode to count new content
  private readbackStartHeight = 0;
  // Track last known scroll position to detect user-initiated scrolls
  private lastScrollTop = 0;
  // Track last known client height to detect viewport resizes (not user scrolls)
  private lastClientHeight = 0;
  // Track when we last programmatically set scrollTop - ignore scroll-up events shortly after
  private lastProgrammaticScrollTime = 0;
  // Grace period after programmatic scroll during which we ignore scroll-up events
  private readonly PROGRAMMATIC_SCROLL_GRACE_MS = 200;
  // Track if we were at bottom on the previous scroll event
  private wasAtBottomOnPreviousScroll = true;
  // ResizeObserver for viewport (clientHeight) changes
  private viewportResizeObserver: ResizeObserver | null = null;
  // Saved scroll position for restoring after dockview tab show (display:none → visible)
  private savedScrollTop: number | null = null;
  private visibilityMutationObserver: MutationObserver | null = null;
  private wasHidden = false;

  @Input()
  set messages(value: ChatService.ChatMessage[]) {
    // Skip if same reference (no actual change)
    if (value === this._messages) {
      return;
    }
    this._messages = value;

    // Trigger CD since we might be outside Angular zone
    this.cdr.detectChanges();

    // Setup content resize observer if not already done (needs .convo to exist)
    this.setupContentResizeObserver();

    // Load dynamic components after DOM updates, debounced to avoid rapid updates during streaming
    if (!this.isProcessingStream) {
      this.isProcessingStream = true;

      requestAnimationFrame(() => {
        this.loadDynamicComponents();
        this.isProcessingStream = false;
      });
    }
  }
  
  get messages(): ChatService.ChatMessage[] {
    return this._messages;
  }
  
  private _messages: ChatService.ChatMessage[] = [];
  @Input() loading = false;
  @Input() loadingState: 'connecting' | 'generating' | 'stalled' | '' = '';
  @Input() hideTypingDots = false;
  // Raw terminal spinner line (e.g. "✻ Actualizing… (esc to interrupt · 3s · ↑ 1.2k tokens)")
  // shown verbatim during the pre-body think-gap, in place of the throbbing dots.
  @Input() spinnerText = '';
  @Input() embed = false;
  @Input() hasMoreMessages = false;
  @Input() loadingMoreMessages = false;
  @Input() sessionId: string | undefined; // Session ID for saving screenshots
  @Input() channelId: string | undefined; // Channel ID for channel pin/bookmark operations
  @Input() teamName: string | undefined; // Team the channel lives in — a panel can show a team other than the selected one
  @Input() basePath: string | null | undefined; // Channel working folder — relative paths in replies resolve against it
  @Input() suppressScrollButton = false;
  @Output() onLoadMore = new EventEmitter<void>();
  @Output() onOpenWidget = new EventEmitter<{ widget: string }>();
  @Output() onEditFile = new EventEmitter<{ path: string }>();
  @Output() onViewMarkdown = new EventEmitter<{ path: string; title: string }>();
  @Output() onViewCsv = new EventEmitter<{ path: string; title: string }>();
  @Output() onPreviewFile = new EventEmitter<{ path: string; previewType: 'image' | 'pdf' }>();
  /** User clicked an option on an interactive AskUserQuestion (AICS bug-3). */
  @Output() answerQuestion = new EventEmitter<{ question: string; answer: string }>();
  @Output() onStopRequested = new EventEmitter<void>();
  @Output() messageDeleted = new EventEmitter<ChatService.ChatMessage>();
  @Output() onCreateWorkSession = new EventEmitter<{ type: string; title: string; summary?: string; warm_up?: string }>();
  @Output() onExpandMessage = new EventEmitter<ChatService.ChatMessage>();

  public $loadMore(): void {
    this.onLoadMore.emit();
  }

  // Reaction state
  public reactionEmojis = REACTION_EMOJIS;
  public activePickerMessageId: string | null = null;

  public get reactionsEnabled(): boolean {
    return this._settings.labsReactions;
  }

  // Header customization — ordered items with favorites promoted to header bar
  private static FAVORITES_KEY = 'lit-header-favorites';
  private static ORDER_KEY = 'lit-header-order';
  private static DEFAULT_FAVORITES = ['agent', 'model', 'timestamp', 'moon'];   // the model that answered is shown on every reply by default (2026-10-18)
  private static DEFAULT_ORDER = ['agent', 'orchestration', 'duration', 'tokens', 'copy', 'flag', 'path', 'transcript', 'session', 'moon', 'model', 'timestamp', 'pin', 'delete'];
  private static MENU_ICONS: Record<string, string> = {
    agent: 'smart_toy', orchestration: 'account_tree', duration: 'speed', tokens: 'toll', copy: 'content_copy', flag: 'flag',
    path: 'folder', transcript: 'visibility', session: 'tag', moon: 'dark_mode', model: 'psychology',
    timestamp: 'schedule', pin: 'push_pin', delete: 'delete',
  };

  public headerFavorites: Set<string>;
  public headerItemOrder: string[];

  constructor(
    private _snackBar: MatSnackBar,
    private _router: Router,
    private _identity: IdentityService,
    private _muxSession: MuxSessionService,
    private _settings: SettingsService,
    private componentFactoryResolver: ComponentFactoryResolver,
    private cdr: ChangeDetectorRef
  ) {
    const storedFavs = localStorage.getItem(ChatScreenComponent.FAVORITES_KEY);
    const known = ChatScreenComponent.DEFAULT_ORDER;
    this.headerFavorites = new Set(
      storedFavs ? (JSON.parse(storedFavs) as string[]).filter(id => known.includes(id)) : ChatScreenComponent.DEFAULT_FAVORITES
    );
    const storedOrder = localStorage.getItem(ChatScreenComponent.ORDER_KEY);
    if (storedOrder) {
      const parsed: string[] = (JSON.parse(storedOrder) as string[]).filter(id => known.includes(id));
      // Merge in any new items not yet in stored order
      const missing = ChatScreenComponent.DEFAULT_ORDER.filter(id => !parsed.includes(id));
      this.headerItemOrder = [...parsed, ...missing];
    } else {
      this.headerItemOrder = [...ChatScreenComponent.DEFAULT_ORDER];
    }
  }

  public isFavorite(id: string): boolean {
    return this.headerFavorites.has(id);
  }

  public toggleFavorite(id: string, event: Event): void {
    event.stopPropagation();
    if (this.headerFavorites.has(id)) {
      this.headerFavorites.delete(id);
    } else {
      this.headerFavorites.add(id);
    }
    localStorage.setItem(ChatScreenComponent.FAVORITES_KEY, JSON.stringify([...this.headerFavorites]));
    this.cdr.markForCheck();
  }

  public getMenuIcon(id: string, turn?: ChatService.ChatMessage): string {
    return ChatScreenComponent.MENU_ICONS[id] || '';
  }

  public getMenuLabel(id: string, turn: ChatService.ChatMessage): string {
    const t = turn as any;
    switch (id) {
      case 'agent': return t.agentName || '';
      case 'orchestration': return t.orchestration || '';
      case 'duration': return this.latencyTooltip(t);
      case 'tokens': return this.tokensTooltip(t);
      case 'copy': return 'Copy content';
      case 'flag': return 'Flag for QA';
      case 'path': return 'Copy path';
      case 'transcript': return 'Inspect turn';
      case 'session': return 'Copy session ID';
      case 'moon': return 'Sleep status';
      case 'model': return t.model || '';
      case 'timestamp': return 'Timestamp';
      case 'pin': return turn.pinned ? 'Unpin' : 'Pin';
      case 'delete': return 'Delete message';
      default: return '';
    }
  }

  public isItemVisible(id: string, turn: ChatService.ChatMessage): boolean {
    const t = turn as any;
    switch (id) {
      case 'agent': return !!t.agentName;
      case 'orchestration': return !!t.orchestration;
      case 'duration': return !!t.durationMs;
      case 'tokens': return t.inputTokens != null || t.outputTokens != null;
      case 'copy': return true;
      case 'flag': return !!this.channelId && !turn.isSelf;
      case 'path': return !!t.turnPath;
      case 'transcript': return !!t.transcriptPath;
      case 'session': return !!t.sessionId;
      case 'moon': return !!t.sleepReason;
      case 'model': return !!t.model;
      case 'timestamp': return !!t.timestamp;
      case 'pin': return !!t.messageId && (!!this.channelId || !!this.sessionId);
      case 'delete': return !!this.channelId && !!t.messageId;
      default: return false;
    }
  }

  public onMenuAction(id: string, turn: ChatService.ChatMessage): void {
    const t = turn as any;
    switch (id) {
      case 'copy': this.copyToClipboard(turn.content as string); break;
      case 'flag': this.flagMessageForQA(turn); break;
      case 'path': if (t.turnPath) this.copyTurnPath(t.turnPath); break;
      case 'transcript': if (t.transcriptPath) this.handleOpenMarkdownFile({ path: t.transcriptPath, title: 'Transcript' }); break;
      case 'session': if (t.sessionId) this.copySessionId(t.sessionId); break;
      case 'pin': this.togglePin(turn); break;
      case 'delete': this.deleteMessage(turn); break;
    }
  }

  public moveItem(id: string, direction: number, turn: ChatService.ChatMessage, event: Event): void {
    event.stopPropagation();
    const arr = this.headerItemOrder;
    const idx = arr.indexOf(id);
    if (idx < 0) return;
    // Find the next visible item in the given direction (skip hidden items)
    let targetIdx = idx + direction;
    while (targetIdx >= 0 && targetIdx < arr.length) {
      if (this.isItemVisible(arr[targetIdx], turn)) break;
      targetIdx += direction;
    }
    if (targetIdx < 0 || targetIdx >= arr.length) return;
    [arr[idx], arr[targetIdx]] = [arr[targetIdx], arr[idx]];
    localStorage.setItem(ChatScreenComponent.ORDER_KEY, JSON.stringify(arr));
    this.cdr.markForCheck();
  }

  /**
   * Manually trigger change detection.
   * Call this from parent after mutating message content (e.g., during streaming).
   */
  public refresh(): void {
    this.cdr.markForCheck();
  }

  /**
   * Set the bottom padding on the conversation content.
   * Used to reserve space behind the overlapping input area.
   */
  public setContentPaddingBottom(px: number): void {
    this._content.nativeElement.style.paddingBottom = `${px}px`;
  }

  ngOnInit(): void {
    // Don't call loadDynamicComponents here, it will be called after view has initialized
  }

  ngAfterViewInit(): void {
    // Initialize components after view is ready
    this.loadDynamicComponents();

    // Also listen for changes to dynamicHosts to load new components
    if (this.dynamicHosts) {
      this.dynamicHosts.changes.subscribe(() => {
        this.loadDynamicComponents();
      });
    }

    // Setup ResizeObserver for widget containers
    this.setupWidgetResizeObserver();

    // Setup ResizeObserver for content height changes
    this.setupContentResizeObserver();

    // Track scroll position to detect readback mode
    this.boundScrollHandler = this.onScroll.bind(this);
    this.lastClientHeight = this._content.nativeElement.clientHeight;
    this._content.nativeElement.addEventListener('scroll', this.boundScrollHandler);

    // Setup ResizeObserver for viewport (clientHeight) changes
    // This fires BEFORE scroll events, allowing us to compensate proactively
    this.setupViewportResizeObserver();

    // Watch for dockview tab show/hide: save scrollTop before display:none, restore after
    this.setupVisibilityObserver();

    // Browser tab background → foreground: while hidden, layout/rAF are throttled
    // (clientHeight can read 0), so the "stay pinned to bottom" observer can't keep
    // up as content streams in — you return scrolled off with a "new" pill. If you
    // were pinned to the bottom when you left (not in readback mode), re-assert it
    // on return (Ben, 2026-08-21: flip to the desktop app, come back in scrollback).
    this.boundVisibilityHandler = () => {
      if (document.visibilityState === 'visible') this.reassertBottomIfPinned();
    };
    document.addEventListener('visibilitychange', this.boundVisibilityHandler);
  }

  /** If the user was at the bottom (not reading back), snap to the true bottom.
   *  Safe no-op while in readback mode, so it never fights a deliberate scroll-up. */
  private reassertBottomIfPinned(): void {
    if (this.isInReadbackMode) return;
    const el = this._content?.nativeElement;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    this.lastScrollTop = el.scrollTop;
    this.lastKnownScrollHeight = el.scrollHeight;
    this.lastProgrammaticScrollTime = Date.now();
    this.wasAtBottomOnPreviousScroll = true;
  }

  ngOnDestroy(): void {
    // Cleanup scroll listener
    if (this.boundScrollHandler) {
      this._content.nativeElement.removeEventListener('scroll', this.boundScrollHandler);
      this.boundScrollHandler = null;
    }
    if (this.boundVisibilityHandler) {
      document.removeEventListener('visibilitychange', this.boundVisibilityHandler);
      this.boundVisibilityHandler = null;
    }

    // Cleanup ResizeObservers
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
      this.widgetResizeObserver = null;
    }
    this.observedWidgets.clear();

    if (this.contentResizeObserver) {
      this.contentResizeObserver.disconnect();
      this.contentResizeObserver = null;
    }

    if (this.viewportResizeObserver) {
      this.viewportResizeObserver.disconnect();
      this.viewportResizeObserver = null;
    }

    if (this.visibilityMutationObserver) {
      this.visibilityMutationObserver.disconnect();
      this.visibilityMutationObserver = null;
    }
  }

  /**
   * Watch ancestors for display:none transitions (dockview tab switching).
   * Saves scrollTop before the element is hidden; restores it when it becomes visible again.
   * Without this, browsers reset scrollTop to 0 when an element is display:none.
   */
  private setupVisibilityObserver(): void {
    const el = this._content.nativeElement;

    // Walk up to find the nearest ancestor that dockview toggles display on
    // (typically the panel content container, a few levels up)
    const findToggledAncestor = (): Element | null => {
      let node: Element | null = el.parentElement;
      while (node && node !== document.body) {
        if (getComputedStyle(node).display === 'none') return node;
        node = node.parentElement;
      }
      return null;
    };

    this.visibilityMutationObserver = new MutationObserver(() => {
      const isHidden = !el.offsetParent && getComputedStyle(el).display === 'none' ||
        !!findToggledAncestor();

      if (isHidden && !this.wasHidden) {
        // Transitioning visible → hidden: save scroll position NOW, before browser zeroes it
        this.savedScrollTop = el.scrollTop;
        this.wasHidden = true;
      } else if (!isHidden && this.wasHidden) {
        // Transitioning hidden → visible. If the user was pinned to the bottom
        // when the panel hid, content that streamed in meanwhile means the saved
        // absolute scrollTop is no longer the bottom — snap to the real bottom
        // instead. Only restore the exact offset when they were reading back.
        this.wasHidden = false;
        if (!this.isInReadbackMode) {
          this.reassertBottomIfPinned();
          this.savedScrollTop = null;
        } else if (this.savedScrollTop !== null) {
          el.scrollTop = this.savedScrollTop;
          this.lastScrollTop = this.savedScrollTop;
          this.savedScrollTop = null;
        }
      }
    });

    // Observe the parent chain for style/class changes (dockview uses both)
    let node: Element | null = el.parentElement;
    for (let i = 0; i < 8 && node && node !== document.body; i++) {
      this.visibilityMutationObserver.observe(node, {
        attributes: true,
        attributeFilter: ['style', 'class']
      });
      node = node.parentElement;
    }
  }

  /**
   * Setup ResizeObserver to auto-scroll when widget containers are resized
   */
  private setupWidgetResizeObserver(): void {
    this.widgetResizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const container = entry.target as HTMLElement;
        const contentEl = this._content.nativeElement;

        // Calculate where the bottom of the widget is relative to the scroll container
        const containerRect = container.getBoundingClientRect();
        const contentRect = contentEl.getBoundingClientRect();

        // How far below the visible area is the widget bottom?
        const widgetBottom = containerRect.bottom;
        const viewportBottom = contentRect.bottom;

        // If widget bottom is below the viewport, scroll to show it
        if (widgetBottom > viewportBottom) {
          const scrollAmount = widgetBottom - viewportBottom;
          contentEl.scrollTop += scrollAmount;
        }
      }
    });

    // Observe existing widget containers
    this.observeWidgetContainers();

    // Use MutationObserver to detect new widget containers
    const mutationObserver = new MutationObserver(() => {
      this.observeWidgetContainers();
    });

    mutationObserver.observe(this._content.nativeElement, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Setup ResizeObserver to scroll to bottom when content height increases.
   * This handles late layout changes (images loading, dynamic components rendering).
   * We observe the inner .convo element since it's the one that actually grows.
   */
  private setupContentResizeObserver(): void {
    // Already setup
    if (this.contentResizeObserver) return;

    const scrollContainer = this._content.nativeElement;
    const convo = scrollContainer.querySelector('.convo');
    if (!convo) return; // No content yet - will retry when messages are set

    this.lastKnownScrollHeight = scrollContainer.scrollHeight;

    this.contentResizeObserver = new ResizeObserver(() => {
      const newScrollHeight = scrollContainer.scrollHeight;

      // Only act if content grew (not shrunk)
      if (newScrollHeight > this.lastKnownScrollHeight) {
        // If we're not in readback mode and were at the bottom, stay at bottom
        if (!this.isInReadbackMode) {
          const distanceFromBottom = newScrollHeight - scrollContainer.scrollTop - scrollContainer.clientHeight;
          // If we were "close enough" to bottom before growth, scroll to new bottom
          if (distanceFromBottom <= this.SCROLL_THRESHOLD + (newScrollHeight - this.lastKnownScrollHeight)) {
            scrollContainer.scrollTop = newScrollHeight;
            this.lastScrollTop = scrollContainer.scrollTop;
            this.lastProgrammaticScrollTime = Date.now();
          }
        }
      }

      this.lastKnownScrollHeight = newScrollHeight;
    });

    // Observe the inner convo element - its size changes when content grows
    this.contentResizeObserver.observe(convo);
  }

  /**
   * Setup ResizeObserver for the viewport (scroll container) itself.
   * This detects clientHeight changes (e.g., when textarea grows/shrinks)
   * and compensates scroll position to maintain distance from bottom.
   */
  private setupViewportResizeObserver(): void {
    if (this.viewportResizeObserver) return;

    const scrollContainer = this._content.nativeElement;
    let lastObservedClientHeight = scrollContainer.clientHeight;
    let lastObservedClientWidth = scrollContainer.clientWidth;

    this.viewportResizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newClientHeight = entry.contentRect.height;
        const newClientWidth = entry.contentRect.width;

        const heightChanged = newClientHeight !== lastObservedClientHeight;
        const widthChanged = newClientWidth !== lastObservedClientWidth;

        if (!heightChanged && !widthChanged) continue;

        if (heightChanged) {
          const heightDelta = lastObservedClientHeight - newClientHeight; // positive if viewport shrank
          const currentScrollTop = scrollContainer.scrollTop;
          const wasNearBottom = (scrollContainer.scrollHeight - currentScrollTop - lastObservedClientHeight) <= this.SCROLL_THRESHOLD;

          // If we were near bottom and viewport shrank, scroll down to stay at bottom
          if (wasNearBottom && heightDelta > 0) {
            const newScrollTop = currentScrollTop + heightDelta;
            const maxScrollTop = scrollContainer.scrollHeight - newClientHeight;
            const clampedScrollTop = Math.min(newScrollTop, maxScrollTop);

            scrollContainer.scrollTop = clampedScrollTop;
            this.lastScrollTop = clampedScrollTop;
            this.lastProgrammaticScrollTime = Date.now();
          }
        }

        if (widthChanged && !this.isInReadbackMode) {
          // Width change causes text to reflow — scrollHeight changes but scrollTop
          // stays the same, making the user appear to have scrolled up. Pin to bottom.
          scrollContainer.scrollTop = scrollContainer.scrollHeight;
          this.lastScrollTop = scrollContainer.scrollTop;
          this.lastProgrammaticScrollTime = Date.now();
        }

        lastObservedClientHeight = newClientHeight;
        lastObservedClientWidth = newClientWidth;
        this.lastClientHeight = newClientHeight;
      }
    });

    this.viewportResizeObserver.observe(scrollContainer);
  }

  /**
   * Find and observe all widget containers that haven't been observed yet
   */
  private observeWidgetContainers(): void {
    if (!this.widgetResizeObserver) return;

    const containers = this._content.nativeElement.querySelectorAll('.widget-container');
    containers.forEach((container) => {
      if (!this.observedWidgets.has(container)) {
        this.widgetResizeObserver!.observe(container);
        this.observedWidgets.add(container);
      }
    });
  }

  // Use a Map to track which messages have already been rendered
  private renderedMessageMap = new Map<number, boolean>();
  // Track when a loading operation is in progress
  private isLoadingComponents = false;

  loadDynamicComponents(): void {
    // Prevent concurrent loading operations
    if (this.isLoadingComponents) {
      return;
    }
    
    // Guard against dynamicHosts being undefined
    if (!this.dynamicHosts || !this.messages || this.messages.length === 0) {
      return;
    }

    this.isLoadingComponents = true;
    
    try {
      // Create an array to match hosts with messages that have componentTypes
      const messagesToRender = this.messages
        .map((message, index) => ({ message, index }))
        .filter(item => item.message && item.message.componentType && !item.message.isStreaming);

      // Only process if we have both hosts and messages with components
      if (this.dynamicHosts.length === 0 || messagesToRender.length === 0) {
        this.isLoadingComponents = false;
        return;
      }

      // For each host, try to render a matching message component
      this.dynamicHosts.forEach((host, hostIndex) => {
        // Map the host index to the corresponding message with a component
        if (hostIndex < messagesToRender.length) {
          const { message, index: messageIndex } = messagesToRender[hostIndex];
          
          // Check if we've already rendered this message or if it's still streaming
          if (this.renderedMessageMap.get(messageIndex)) {
            return;
          }

          try {
            // Clear the container before creating a new component
            host.viewContainerRef.clear();

            // Create the component
            const componentRef = host.viewContainerRef.createComponent<BaseWidgetComponent>(message.componentType);
            const instance = componentRef.instance;
            instance.teamName = this._channelTeam;

            // Apply any component inputs from the message
            if ((message as any).componentInputs) {
              const inputs = (message as any).componentInputs;
              for (const key of Object.keys(inputs)) {
                (instance as any)[key] = inputs[key];
              }
              // Trigger change detection so the component sees the new inputs
              componentRef.changeDetectorRef.detectChanges();
            }

            // Subscribe to common widget outputs that need to bubble up
            // Check if the instance has these outputs before subscribing
            if ((instance as any).onOpenBackendManager) {
              (instance as any).onOpenBackendManager.subscribe(() => {
                this.onOpenWidget.emit({ widget: 'BackendManagerWidget' });
              });
            }
            if ((instance as any).onOpenBackendDetail) {
              (instance as any).onOpenBackendDetail.subscribe(() => {
                this.onOpenWidget.emit({ widget: 'BackendDetailWidget' });
              });
            }
            if ((instance as any).onOpenWidget) {
              (instance as any).onOpenWidget.subscribe((event: { widget: string }) => {
                this.onOpenWidget.emit(event);
              });
            }
            // FilesWidget outputs for opening files
            if ((instance as any).onEditFile) {
              (instance as any).onEditFile.subscribe((event: { path: string }) => {
                this.onEditFile.emit(event);
              });
            }
            if ((instance as any).onViewMarkdown) {
              (instance as any).onViewMarkdown.subscribe((event: { path: string; title: string }) => {
                this.onViewMarkdown.emit(event);
              });
            }
            if ((instance as any).onViewCsv) {
              (instance as any).onViewCsv.subscribe((event: { path: string; title: string }) => {
                this.onViewCsv.emit(event);
              });
            }
            if ((instance as any).onPreviewFile) {
              (instance as any).onPreviewFile.subscribe((event: { path: string; previewType: 'image' | 'pdf' }) => {
                this.onPreviewFile.emit(event);
              });
            }

            // Mark this message as rendered
            this.renderedMessageMap.set(messageIndex, true);

            // Move widget's custom titlebar content to the message header
            this.moveWidgetToolbarToHeader(message);

            // Start capturing screenshot - show loading overlay immediately
            // This makes snapshot capture part of the widget "loading" experience
            (message as any).isCapturingScreenshot = true;
            this.cdr.detectChanges();

            // Capture screenshot when widget is ready
            // Poll for isLoading=false, then use requestIdleCallback
            const element = host.viewContainerRef.element.nativeElement.parentElement;
            this.waitForWidgetReady(instance, () => {
              if ('requestIdleCallback' in window) {
                (window as any).requestIdleCallback(() => {
                  this.captureWidgetScreenshot(element, message);
                }, { timeout: 10000 });
              } else {
                this.captureWidgetScreenshot(element, message);
              }
            });
          } catch (error) {
            console.error('Error creating dynamic component:', error);
          }
        }
      });
    } finally {
      // Always reset the loading flag
      this.isLoadingComponents = false;
    }
  }

  /**
   * Capture a screenshot of a widget element and store it in the message
   * Also saves to server if sessionId and messageId are available
   */
  private async captureWidgetScreenshot(element: HTMLElement, message: ChatService.ChatMessage): Promise<void> {
    if (!element || message.widgetScreenshot) return;

    // Set loading state (may already be set by caller, but ensure it's set for auto-capture)
    if (!(message as any).isCapturingScreenshot) {
      (message as any).isCapturingScreenshot = true;
      this.cdr.detectChanges();
    }

    try {
      // Use html-to-image - faster than html2canvas, less main thread blocking
      // Pre-compute font embedding CSS to include Material Icons
      let fontEmbedCSS: string | undefined;
      try {
        fontEmbedCSS = await htmlToImage.getFontEmbedCSS(element);
      } catch (fontError) {
        console.warn('Failed to embed fonts, proceeding without:', fontError);
      }

      const screenshot = await htmlToImage.toPng(element, {
        backgroundColor: null,
        pixelRatio: 1,
        skipAutoScale: true,
        fontEmbedCSS,  // Use pre-computed font CSS if available
        filter: (node: HTMLElement) => {
          // Exclude the loading overlay from screenshot
          if (node.classList?.contains('widget-loading-overlay')) return false;
          return true;
        }
      });
      message.widgetScreenshot = screenshot;
      console.log('Widget screenshot captured:', message.title);

      // Save screenshot to server if we have session and message IDs
      if (this.sessionId && message.messageId) {
        try {
          await this._muxSession.updateMessageMetadata(
            this.sessionId,
            message.messageId,
            { screenshot }
          );
          console.log('Screenshot saved to server for message:', message.messageId);
        } catch (error) {
          console.warn('Failed to save screenshot to server:', error);
          // Screenshot still works locally
        }
      }
    } catch (error) {
      console.error('Failed to capture widget screenshot:', error);
    } finally {
      (message as any).isCapturingScreenshot = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Manually take a snapshot of a widget (user-triggered)
   */
  public async takeSnapshot(container: HTMLElement, message: ChatService.ChatMessage): Promise<void> {
    // Show spinner immediately
    (message as any).isCapturingScreenshot = true;
    this.cdr.detectChanges();

    // Clear existing screenshot to force a new capture
    message.widgetScreenshot = undefined;
    await this.captureWidgetScreenshot(container, message);
  }

  /**
   * Show snapshot preview on hover
   */
  public showSnapshotPreview(message: ChatService.ChatMessage, event: MouseEvent): void {
    if (!message.widgetScreenshot) return;

    this.previewingMessage = message;
    // Position preview near the button but offset to not cover it
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    this.previewPosition = {
      x: rect.left - 220, // Show to the left of the button
      y: rect.top - 100
    };
  }

  /**
   * Hide snapshot preview
   */
  public hideSnapshotPreview(): void {
    this.previewingMessage = null;
  }

  /**
   * Move the widget's custom titlebar content (edit/preview buttons, etc.)
   * into the chat message header for a more integrated look.
   */
  private moveWidgetToolbarToHeader(message: ChatService.ChatMessage): void {
    const messageId = message.messageId;
    if (!messageId) return;

    // Use setTimeout to ensure DOM is ready after change detection
    setTimeout(() => {
      // Find the widget container by message ID
      const widgetContainer = this._content.nativeElement.querySelector(
        `.widget-container[data-message-id="${messageId}"]`
      );
      if (!widgetContainer) return;

      const titlebar = widgetContainer.querySelector('app-widget-titlebar');
      if (!titlebar) return;

      const customButtons = titlebar.querySelector('.buttons.custom-buttons');
      if (!customButtons || customButtons.children.length === 0) return;

      // Find the corresponding toolbar slot in the message header
      const toolbarSlot = this._content.nativeElement.querySelector(
        `.widget-toolbar-slot[data-message-id="${messageId}"]`
      );
      if (!toolbarSlot) return;

      // Move all children from customButtons to the toolbar slot
      while (customButtons.firstChild) {
        toolbarSlot.appendChild(customButtons.firstChild);
      }

      // Hide the now-empty titlebar
      (titlebar as HTMLElement).style.display = 'none';
    }, 100);
  }

  /**
   * Wait for widget to finish loading before executing callback.
   * Polls the widget's isLoading property, with fallback timeout.
   */
  private waitForWidgetReady(instance: any, callback: () => void, maxWait = 15000): void {
    const startTime = Date.now();
    const pollInterval = 250; // Check every 250ms

    const checkReady = () => {
      // Widget is ready if it has no isLoading property or isLoading is false
      const isReady = !('isLoading' in instance) || instance.isLoading === false;

      if (isReady) {
        // Trigger change detection so the widget content is visible
        this.cdr.detectChanges();
        // Add small delay after isLoading=false to let DOM settle
        setTimeout(callback, 500);
      } else if (Date.now() - startTime < maxWait) {
        // Keep polling
        setTimeout(checkReady, pollInterval);
      } else {
        // Timeout - capture anyway
        console.warn('Widget loading timeout, capturing screenshot anyway');
        callback();
      }
    };

    // Start polling after initial delay to let widget initialize
    setTimeout(checkReady, 500);
  }

  /**
   * Get the widget container element for a message (used by snapshot button in header)
   */
  public getWidgetContainer(message: ChatService.ChatMessage): HTMLElement | null {
    if (!message.messageId) return null;
    return this._content.nativeElement.querySelector(`[data-message-id="${message.messageId}"]`);
  }

  /**
   * Handle scroll events to detect readback mode.
   * When user scrolls up away from the bottom, enter readback mode (pause auto-scroll).
   * When user scrolls back to the bottom, exit readback mode.
   *
   * Simple logic: only enter readback mode if user scrolled UP (scrollTop decreased).
   */
  private onScroll(): void {
    const el = this._content.nativeElement;
    const currentScrollTop = el.scrollTop;
    const currentClientHeight = el.clientHeight;
    const distanceFromBottom = el.scrollHeight - currentScrollTop - currentClientHeight;
    const previousScrollTop = this.lastScrollTop;
    const previousClientHeight = this.lastClientHeight;

    this.lastScrollTop = currentScrollTop;
    this.lastClientHeight = currentClientHeight;

    // If clientHeight changed, the viewport resized (e.g., textarea grew/shrank).
    // The viewportResizeObserver handles compensation - just ignore this scroll event.
    // Also mark this as a programmatic scroll time to ignore ghost scrolls that follow.
    if (currentClientHeight !== previousClientHeight) {
      this.lastProgrammaticScrollTime = Date.now();
      return;
    }

    // Only enter readback mode if user scrolled UP (scrollTop decreased)
    const scrolledUp = currentScrollTop < previousScrollTop;
    const scrollDelta = previousScrollTop - currentScrollTop;
    const isAtBottom = distanceFromBottom <= this.SCROLL_THRESHOLD;

    // If we're at or near the bottom, exit readback mode and track state
    if (isAtBottom) {
      this.wasAtBottomOnPreviousScroll = true;
      if (this.isInReadbackMode) {
        this.isInReadbackMode = false;
        this.showScrollToBottom = false;
        this.newContentCount = 0;
        this.cdr.detectChanges();
      }
      return;
    }

    // We're NOT at the bottom - only enter readback mode if user scrolled UP
    if (scrolledUp) {
      // Ignore scroll-up events that happen shortly after a programmatic scroll or resize.
      // These are typically "ghost" scrolls from browser layout reflow, not user action.
      const timeSinceProgrammaticScroll = Date.now() - this.lastProgrammaticScrollTime;
      if (timeSinceProgrammaticScroll < this.PROGRAMMATIC_SCROLL_GRACE_MS) {
        this.wasAtBottomOnPreviousScroll = false;
        return;
      }

      // If we were at bottom on the previous scroll event and now jumped away by a large amount,
      // this is almost certainly a ghost scroll - restore to bottom instead of entering readback mode.
      if (this.wasAtBottomOnPreviousScroll && scrollDelta > 100) {
        el.scrollTop = el.scrollHeight - currentClientHeight;
        this.lastScrollTop = el.scrollTop;
        this.lastProgrammaticScrollTime = Date.now();
        this.wasAtBottomOnPreviousScroll = true;
        return;
      }

      if (!this.isInReadbackMode) {
        this.readbackStartHeight = el.scrollHeight;
        this.newContentCount = 0;
      }
      this.isInReadbackMode = true;
      this.showScrollToBottom = true;
      this.cdr.detectChanges();
    }

    // Track that we're not at bottom for the next scroll event
    this.wasAtBottomOnPreviousScroll = false;
  }

  /**
   * Update the new content count when content is added during readback mode.
   * Called from scrollToBottom when new content arrives.
   */
  public updateNewContentCount(): void {
    if (this.isInReadbackMode) {
      const el = this._content.nativeElement;
      // Estimate new lines based on height change (rough: ~24px per line)
      const heightDiff = el.scrollHeight - this.readbackStartHeight;
      this.newContentCount = Math.max(1, Math.floor(heightDiff / 100)); // Count in rough "chunks"
      this.cdr.detectChanges();
    }
  }

  /**
   * Handle click on scroll-to-bottom button.
   */
  public onScrollToBottomClick(): void {
    this.forceScrollToBottom();
  }

  /**
   * Check if currently in readback mode (user scrolled up to read).
   */
  public isReadbackMode(): boolean {
    return this.isInReadbackMode;
  }

  /**
   * Force exit readback mode and scroll to bottom.
   * Call this when user sends a new message.
   */
  public exitReadbackMode(): void {
    this.isInReadbackMode = false;
    this._content.nativeElement.scrollTop = this._content.nativeElement.scrollHeight;
  }

  @debounce(100)
  public scrollToTop(): void {
    this._content.nativeElement.scrollTop = 0;
  }

  @debounce(100)
  /** Receipt tooltip: where a follow-up's answer went, or the plain state. */
  receiptTooltip(turn: any): string {
    if (turn?.handledIn) {
      const t = turn.handledAt ? new Date(turn.handledAt) : null;
      const when = t && !isNaN(t.getTime()) ? t.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '';
      return when ? `Handled in the reply at ${when} — click to jump` : 'Handled in an earlier reply — click to jump';
    }
    return turn?.read ? 'Read' : 'Delivered';
  }

  /** Jump to a message by id (the turn element carries data-message-id). */
  scrollToMessage(messageId: string): void {
    const el = this._content?.nativeElement?.querySelector(`[data-message-id="${messageId}"]`) as HTMLElement | null;
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('jump-flash');
    setTimeout(() => el.classList.remove('jump-flash'), 1600);
  }

  public scrollToBottom(): void {
    // Don't auto-scroll if user is in readback mode (scrolled up to read)
    if (this.isInReadbackMode) {
      // Update the badge count to show new content is available
      this.updateNewContentCount();
      return;
    }
    const el = this._content.nativeElement;
    el.scrollTop = el.scrollHeight;
    this.lastScrollTop = el.scrollTop;
  }

  /**
   * Force scroll to bottom regardless of readback mode.
   * Use for user-initiated actions like sending a message.
   */
  @debounce(100)
  public forceScrollToBottom(): void {
    this.isInReadbackMode = false;
    this.showScrollToBottom = false;
    this.newContentCount = 0;
    const el = this._content.nativeElement;
    el.scrollTop = el.scrollHeight;
    this.lastScrollTop = el.scrollTop;
    this.cdr.detectChanges();
  }

  @debounce(100)
  public scrollToAnchor(anchorName: string): void {
    const anchor = this._content.nativeElement.querySelector(`a[name="${anchorName}"]`);
    if (anchor) {
      anchor.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }

  public copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this._snackBar.open('Copied to clipboard!', 'OK', { duration: 2000 });
    }).catch(err => {
      console.error('Failed to copy text:', err);
    });
  }

  public copyTurnPath(turnPath: string): void {
    navigator.clipboard.writeText(turnPath).then(() => {
      this._snackBar.open('Message path copied!', 'OK', { duration: 2000 });
    }).catch(err => {
      console.error('Failed to copy turn path:', err);
    });
  }

  public copySessionId(sessionId: string): void {
    navigator.clipboard.writeText(sessionId).then(() => {
      this._snackBar.open('Session ID copied!', 'OK', { duration: 2000 });
    }).catch(err => {
      console.error('Failed to copy session ID:', err);
    });
  }

  public formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    const seconds = ms / 1000;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.round(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  }

  /** Show the first-content (FCP) half of the latency chip only when it's
   *  meaningfully later than first-byte (TTFB) — i.e. there was a visible
   *  think/tool gap. Direct answers (sub-500ms delta) collapse to just TTFB so
   *  the chip doesn't show two near-identical numbers. */
  public showFcp(turn: any): boolean {
    return turn.fcpMs != null && turn.durationMs != null && (turn.fcpMs - turn.durationMs) >= 500;
  }

  /** Tooltip for the latency chip: first byte, plus first content when the two
   *  meaningfully differ. */
  public latencyTooltip(turn: any): string {
    const ttfb = `Time to first byte ${this.formatDuration(turn.durationMs)}`;
    if (this.showFcp(turn)) {
      return `${ttfb} · first content ${this.formatDuration(turn.fcpMs)}`;
    }
    return ttfb;
  }

  /** Compact token count: 936 → "936", 3736 → "3.7k", 1_200_000 → "1.2M". */
  public formatTokens(n: number | null | undefined): string {
    if (n == null) return '0';
    if (n < 1000) return `${n}`;
    if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
    return `${(n / 1_000_000).toFixed(1)}M`;
  }

  /** Badge text: input↓ / output↑ tokens, e.g. "↓3.7k ↑856". */
  public formatTokenBadge(turn: any): string {
    return `↓${this.formatTokens(turn.inputTokens)} ↑${this.formatTokens(turn.outputTokens)}`;
  }

  /** Tooltip with the full breakdown, including cache tokens when present. */
  public tokensTooltip(turn: any): string {
    const parts = [`input ${turn.inputTokens ?? 0}`, `output ${turn.outputTokens ?? 0}`];
    if (turn.cacheTokens != null) parts.push(`cache ${turn.cacheTokens}`);
    return `Tokens — ${parts.join(' · ')}`;
  }

  public formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${Math.round(kb)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  }

  public getTurnLabel(turnPath: string): string {
    // Extract "turn_016" from "/home/ben/.config/lit/agents/.../turn_016.txt"
    const filename = turnPath.split('/').pop() || '';
    return filename.replace('.txt', '');
  }

  /**
   * Toggle pin state for a message. Persists to backend.
   */
  public async togglePin(message: ChatService.ChatMessage): Promise<void> {
    if (!message.messageId) return;
    if (!this.sessionId && !this.channelId) return;

    try {
      if (this.channelId) {
        // Channel-based pinning (DM/channel stores)
        if (message.pinned) {
          await this._muxSession.unpinChannelMessage(this.channelId, message.messageId, this._channelTeam);
          message.pinned = false;
        } else {
          await this._muxSession.pinChannelMessage(this.channelId, message.messageId, this._channelTeam);
          message.pinned = true;
        }
      } else if (this.sessionId) {
        // Session-based pinning (sync chat)
        if (message.pinned) {
          await this._muxSession.unpinMessage(this.sessionId, message.messageId);
          message.pinned = false;
        } else {
          await this._muxSession.pinMessage(this.sessionId, message.messageId);
          message.pinned = true;
        }
      }
      this.pinsChanged.emit();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      this._snackBar.open('Failed to pin message', 'OK', { duration: 2000 });
    }
  }

  // ---- Tag editing state ----
  public editingTag: { message: ChatService.ChatMessage; index: number } | null = null;
  private knownTags: string[] = [];
  private knownTagsLoaded = false;

  private getRef(message: ChatService.ChatMessage): string | null {
    const t = message as any;
    if (!t.turnPath || !this.channelId) return null;
    const parts = t.turnPath.split('/messages/');
    if (parts.length < 2) return null;
    return this.channelId + '/' + parts[1];
  }

  public startTagEdit(message: ChatService.ChatMessage, index: number): void {
    if (!this.channelId || !(message as any).turnPath) return;
    this.editingTag = { message, index };
    this.loadKnownTags();
    this.cdr.detectChanges();
    setTimeout(() => {
      const input = document.querySelector('.tag-edit-input:not([placeholder])') as HTMLInputElement;
      if (input) { input.focus(); input.select(); }
    });
  }

  public startAddTag(message: ChatService.ChatMessage): void {
    if (!this.channelId || !(message as any).turnPath) return;
    this.editingTag = { message, index: -1 };
    this.loadKnownTags();
    this.cdr.detectChanges();
    setTimeout(() => {
      const input = document.querySelector('.tag-edit-input[placeholder]') as HTMLInputElement;
      if (input) input.focus();
    });
  }

  public cancelTagEdit(): void {
    this.editingTag = null;
    this.cdr.detectChanges();
  }

  public async saveTagEdit(message: ChatService.ChatMessage, index: number, newValue: string): Promise<void> {
    if (!this.editingTag) return;
    this.editingTag = null;
    const t = message as any;
    const links: string[] = t.links || [];
    const oldTag = links[index];
    const tag = newValue.trim().toLowerCase().replace(/[^a-z0-9/\-]+/g, '-').replace(/^-|-$/g, '');
    if (!tag || tag === oldTag) { this.cdr.detectChanges(); return; }

    const ref = this.getRef(message);
    if (!ref) return;

    try {
      await this._muxSession.tagMessage(ref, oldTag, 'remove', this._channelTeam);
      await this._muxSession.tagMessage(ref, tag, 'add', this._channelTeam);
      links[index] = tag;
      t.links = [...links];
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    } catch (error) {
      this._snackBar.open('Failed to update tag', 'OK', { duration: 2000 });
    }
  }

  public async deleteTag(message: ChatService.ChatMessage, index: number, event: MouseEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    this.editingTag = null;
    const t = message as any;
    const links: string[] = t.links || [];
    const tag = links[index];
    const ref = this.getRef(message);
    if (!ref) return;

    try {
      await this._muxSession.tagMessage(ref, tag, 'remove', this._channelTeam);
      links.splice(index, 1);
      t.links = [...links];
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    } catch (error) {
      this._snackBar.open('Failed to remove tag', 'OK', { duration: 2000 });
    }
  }

  public async saveNewTag(message: ChatService.ChatMessage, value: string): Promise<void> {
    if (!this.editingTag) return;
    this.editingTag = null;
    const tag = value.trim().toLowerCase().replace(/[^a-z0-9/\-]+/g, '-').replace(/^-|-$/g, '');
    if (!tag) { this.cdr.detectChanges(); return; }

    const t = message as any;
    const ref = this.getRef(message);
    if (!ref) return;

    try {
      await this._muxSession.tagMessage(ref, tag, 'add', this._channelTeam);
      t.links = [...(t.links || []), tag];
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    } catch (error) {
      this._snackBar.open('Failed to add tag', 'OK', { duration: 2000 });
    }
  }

  public getTagSuggestions(input: string): string[] {
    if (!input || input.length < 1) return this.knownTags.slice(0, 8);
    const q = input.toLowerCase();
    return this.knownTags.filter(t => t.includes(q)).slice(0, 8);
  }

  private async loadKnownTags(): Promise<void> {
    if (this.knownTagsLoaded) return;
    try {
      const resp: any = await this._muxSession.getKnowledgeGraph(this.channelId);
      this.knownTags = (resp.nodes || []).map((n: any) => n.id).sort();
      this.knownTagsLoaded = true;
    } catch { /* ignore */ }
  }

  /** Team for channel operations: the hosting panel's team, else the selected one. */
  private get _channelTeam(): string | undefined {
    return this.teamName || this._identity.me?.selectedTeamName;
  }

  public async deleteMessage(message: ChatService.ChatMessage): Promise<void> {
    if (!message.messageId || !this.channelId) return;
    try {
      await this._muxSession.deleteChannelMessage(this.channelId, message.messageId, this._channelTeam);
      this.messageDeleted.emit(message);
      this._snackBar.open('Message deleted', 'OK', { duration: 2000 });
    } catch (error: any) {
      console.error('Failed to delete message:', error);
      const refused = error?.status === 423 && error?.error?.detail;
      this._snackBar.open(refused || 'Failed to delete message', 'OK', { duration: refused ? 8000 : 2000 });
    }
  }

  public async flagMessageForQA(message: ChatService.ChatMessage): Promise<void> {
    if (!message.messageId || !this.channelId) return;
    try {
      await this._muxSession.flagChannelMessage(this.channelId, message.messageId, this._channelTeam);
      this._snackBar.open('Flagged for QA', 'OK', { duration: 2000 });
    } catch (error) {
      console.error('Failed to flag message:', error);
      this._snackBar.open('Failed to flag message', 'OK', { duration: 2000 });
    }
  }

  /**
   * Toggle the reaction picker for a message.
   */
  public toggleReactionPicker(messageId: string): void {
    this.activePickerMessageId = this.activePickerMessageId === messageId ? null : messageId;
    this.cdr.detectChanges();
  }

  /**
   * Toggle a user reaction on a message. Persists to backend.
   */
  public async toggleReaction(message: ChatService.ChatMessage, emoji: string): Promise<void> {
    if (!message.messageId) return;
    if (!this.sessionId && !this.channelId) return;

    // Optimistic update — show/hide the reaction immediately
    const reactions = message.reactions || [];
    const existingIdx = reactions.findIndex(r => r.emoji === emoji && r.reactor === 'user');
    const previousReactions = [...reactions];
    if (existingIdx >= 0) {
      reactions.splice(existingIdx, 1);
    } else {
      reactions.push({ emoji, reactor: 'user', timestamp: new Date().toISOString() });
    }
    message.reactions = [...reactions];
    this.activePickerMessageId = null;
    this.cdr.detectChanges();

    // Fire server call in background — roll back on error
    try {
      let updated: any[];
      if (this.channelId) {
        updated = await this._muxSession.toggleChannelReaction(
          this.channelId,
          message.messageId,
          emoji,
          'user',
          this._channelTeam
        );
      } else {
        updated = await this._muxSession.toggleReaction(
          this.sessionId!,
          message.messageId,
          emoji,
          'user'
        );
      }
      // Reconcile with server truth
      message.reactions = updated;
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Failed to toggle reaction:', error);
      // Roll back to previous state
      message.reactions = previousReactions;
      this.cdr.detectChanges();
    }
  }

  public emojiTooltip(emoji: string): string {
    return REACTION_TOOLTIPS[emoji] || '';
  }

  /**
   * Check if the current user has reacted with a specific emoji.
   */
  public hasUserReaction(reactions: { emoji: string; reactor: string }[], emoji: string): boolean {
    return reactions?.some(r => r.emoji === emoji && r.reactor === 'user') ?? false;
  }

  /**
   * Group reactions by emoji for display. Returns [{emoji, count, hasUser}].
   */
  public getGroupedReactions(reactions: { emoji: string; reactor: string }[]): { emoji: string; count: number; hasUser: boolean }[] {
    if (!reactions?.length) return [];
    const groups = new Map<string, { count: number; hasUser: boolean }>();
    for (const r of reactions) {
      const existing = groups.get(r.emoji) || { count: 0, hasUser: false };
      existing.count++;
      if (r.reactor === 'user') existing.hasUser = true;
      groups.set(r.emoji, existing);
    }
    return Array.from(groups.entries()).map(([emoji, { count, hasUser }]) => ({ emoji, count, hasUser }));
  }

  /**
   * Check if content contains tool call delimiters
   */
  public hasToolCalls(content: string): boolean {
    if (!content || typeof content !== 'string') return false;
    if (content.includes('\x02TOOLJSON')) return true;
    // Legacy: [TOOL_START]🔧 Tool: but NOT preceded by backtick (code span in prose)
    if (/(?<!`)\[TOOL_START\]🔧 Tool:/.test(content)) return true;
    // TUI format from organic relay: ● ToolName(
    if (/^\s*●\s+[A-Z]\w*\(/m.test(content)) return true;
    // Collapsed TUI tool summaries: "  Read 1 file (ctrl+o to expand)"
    if (/^\s{2,}.+\(ctrl\+o to expand\)\s*$/m.test(content)) return true;
    return false;
  }

  /**
   * Strip TUI chrome markers (● response bullets, ✻ completion) for display.
   * Content is preserved in storage — this only affects rendering.
   */
  public stripTuiChrome(content: string): string {
    if (!content || typeof content !== 'string') return content || '';
    return content.replace(/^\s*●\s*/gm, '').trim();
  }

  /**
   * Extract LINKS: wikilink footer from message content.
   * Returns { content: stripped content, links: string[] }.
   */
  public static extractLinks(content: string): { content: string; links: string[] } {
    if (!content || typeof content !== 'string') return { content: content || '', links: [] };
    // The LINKS footer is emitted after all tool calls, so only look for it in
    // the prose tail after the last tool-markup control char. Without this, a
    // LINKS: line embedded in tool output (e.g. a result that cat'd a prior
    // message's own footer) is matched as the footer and, via the greedy
    // `[\s\S]*$`, everything after it — including later tool calls and trailing
    // prose — gets stripped before the message ever reaches the parser.
    const lastMarker = content.lastIndexOf('\x03');
    const base = lastMarker >= 0 ? lastMarker + 1 : 0;
    const tail = content.slice(base);
    // The footer is a LINKS: line of [[wikilinks]] that STARTS its own line; take
    // the last such line and drop what follows it (TUI chrome, system notices,
    // completion markers). An inline mention — the agent writing `The "LINKS:
    // [[…]]" footer is stripped` in the middle of a sentence (abodoo/#skillsuite,
    // 2026-10-29) — is prose: it is preceded by a quote, not a newline, and must
    // not swallow the rest of the reply.
    const footerRe = /(^|\n)LINKS:[ \t]*((?:\[\[[^\]\n]+\]\][ \t]*)+)/g;
    let footer: RegExpExecArray | null = null;
    for (let m = footerRe.exec(tail); m !== null; m = footerRe.exec(tail)) footer = m;
    if (!footer) return { content, links: [] };
    const links = [...footer[2].matchAll(/\[\[([^\]]+)\]\]/g)].map(m => m[1]);
    const after = tail.slice(footer.index + footer[0].length);
    let strippedContent = content.slice(0, base + footer.index).trimEnd();
    // A team-policy notice (budget line, removed integration) is appended by the
    // server after the footer: keep it, it is for the reader. Other trailing
    // content stays dropped.
    const policyNote = after.match(/_ℹ️[^\n]*_/g);
    if (policyNote?.length) strippedContent += '\n\n' + policyNote.join('\n');
    return { content: strippedContent, links };
  }

  /**
   * Handle file path clicks from markdown extensions directive and tool-message component.
   * Routes .md files to the markdown viewer, other files to the code editor.
   */
  private static IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'ico']);
  private static TEXT_EXTENSIONS = new Set([
    'json', 'txt', 'yaml', 'yml', 'xml', 'html', 'htm', 'css', 'scss',
    'js', 'ts', 'jsx', 'tsx', 'py', 'rb', 'sh', 'bash', 'zsh',
    'sql', 'graphql', 'toml', 'ini', 'cfg', 'conf', 'env', 'log',
    'dockerfile', 'makefile', 'gitignore', 'editorconfig',
    'csv', 'eml', 'ics', 'vcf',
  ]);

  public handleOpenMarkdownFile(event: { path: string; title: string }): void {
    const ext = event.path.split('.').pop()?.toLowerCase() || '';
    const isUploadUrl = event.path.includes('/channel-uploads/') || event.path.includes('/uploads/');

    if (ext === 'pdf') {
      this.onPreviewFile.emit({ path: event.path, previewType: 'pdf' });
    } else if (ChatScreenComponent.IMAGE_EXTENSIONS.has(ext)) {
      this.onPreviewFile.emit({ path: event.path, previewType: 'image' });
    } else if (ext === 'md') {
      this.onViewMarkdown.emit(event);
    } else if (ext === 'csv' && !isUploadUrl) {
      this.onViewCsv.emit(event);
    } else if (ChatScreenComponent.TEXT_EXTENSIONS.has(ext) || !isUploadUrl) {
      this.onEditFile.emit({ path: event.path });
    } else {
      window.open(event.path, '_blank', 'noopener');
    }
  }

  /**
   * Handle clicks on content - images open lightbox, internal links use Angular router
   */
  public onContentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Handle image clicks - open lightbox
    if (target.tagName === 'IMG' && target.closest('.message-content')) {
      this.lightboxImageSrc = (target as HTMLImageElement).src;
      this.lightboxOpen = true;
      return;
    }

    // Handle internal link clicks
    const anchor = target.closest('a') as HTMLAnchorElement;
    if (anchor) {
      const href = anchor.getAttribute('href');
      // Check if it's an internal link (starts with /#/ for hash routing)
      if (href && href.startsWith('/#/')) {
        event.preventDefault();

        // Extract the path after /#/ and parse query params
        const fullPath = href.substring(2); // Remove leading /#
        const [path, queryString] = fullPath.split('?');

        // Check if this is a widget deep-link (has ?widget= param)
        if (queryString) {
          const queryParams: { [key: string]: string } = {};
          queryString.split('&').forEach(param => {
            const [key, value] = param.split('=');
            if (key) queryParams[key] = decodeURIComponent(value || '');
          });

          // If it's a widget link, emit event instead of navigating (keeps URL clean)
          if (queryParams['widget']) {
            this.onOpenWidget.emit({ widget: queryParams['widget'] });
            return;
          }

          // Otherwise, navigate with query params
          this._router.navigate([path], { queryParams });
        } else {
          this._router.navigate([path]);
        }
      }
    }
  }

  /**
   * Close lightbox
   */
  public closeLightbox(): void {
    this.lightboxOpen = false;
    this.lightboxImageSrc = '';
  }

  /**
   * Handle Escape key to close lightbox
   */
  @HostListener('document:keydown.escape')
  public onEscapeKey(): void {
    if (this.lightboxOpen) {
      this.closeLightbox();
    }
  }

  @HostListener('document:keydown.pageup', ['$event'])
  public onPageUp(event: KeyboardEvent): void {
    const el = this._content?.nativeElement;
    if (!el) return;
    const target = event.target as HTMLElement;
    if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') return;
    event.preventDefault();
    el.scrollBy({ top: -el.clientHeight * 0.9, behavior: 'smooth' });
  }

  @HostListener('document:keydown.pagedown', ['$event'])
  public onPageDown(event: KeyboardEvent): void {
    const el = this._content?.nativeElement;
    if (!el) return;
    const target = event.target as HTMLElement;
    if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') return;
    event.preventDefault();
    el.scrollBy({ top: el.clientHeight * 0.9, behavior: 'smooth' });
  }

  @HostListener('document:keydown.home', ['$event'])
  public onHomeKey(event: KeyboardEvent): void {
    const el = this._content?.nativeElement;
    if (!el) return;
    const target = event.target as HTMLElement;
    if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') return;
    event.preventDefault();
    el.scrollTo({ top: 0, behavior: 'smooth' });
  }

  @HostListener('document:keydown.end', ['$event'])
  public onEndKey(event: KeyboardEvent): void {
    const el = this._content?.nativeElement;
    if (!el) return;
    const target = event.target as HTMLElement;
    if (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT') return;
    event.preventDefault();
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }

  /**
   * Safely formats a timestamp string into a readable date format
   * Handles ISO strings with or without timezone information
   */
  public formatTimestamp(timestamp: string): string {
    if (!timestamp) return '';
    
    try {
      // If the timestamp doesn't have timezone info, add Z for UTC
      const normalizedTimestamp = timestamp.includes('Z') || timestamp.includes('+') 
        ? timestamp 
        : timestamp + 'Z';
        
      // Create a date object
      const date = new Date(normalizedTimestamp);
      
      // Check if the date is valid
      if (isNaN(date.getTime())) {
        return timestamp; // Return the original if parsing failed
      }
      
      // Format it as a readable string
      return date.toLocaleString();
    } catch (err) {
      console.warn('Error formatting timestamp:', err);
      return timestamp; // Return the original in case of any error
    }
  }

}
